/**
 * ComparisonCard - Shows before/after nutrition comparison
 */

import React from "react";
import { Box, Typography, Grid, Stack } from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import { ACCOUNT_COLORS } from "../../theme/colors";
import type { NutritionOutput } from "../../types";

interface Props {
  previous: NutritionOutput;
  current: NutritionOutput;
  comparison: {
    gramsChange: number;
    gramsChangePercent: number;
    caloriesChange: number;
    caloriesChangePercent: number;
    hasSignificantChange: boolean;
  };
}

interface MetricProps {
  label: string;
  previous: number;
  current: number;
  change: number;
  changePercent: number;
  unit: string;
}

function ComparisonMetric({
  label,
  previous,
  current,
  change,
  changePercent,
  unit,
}: MetricProps) {
  const isIncrease = change > 0;
  const isDecrease = change < 0;
  const isNoChange = change === 0;

  const icon = isIncrease ? (
    <TrendingUpIcon sx={{ fontSize: 20 }} />
  ) : isDecrease ? (
    <TrendingDownIcon sx={{ fontSize: 20 }} />
  ) : (
    <TrendingFlatIcon sx={{ fontSize: 20 }} />
  );

  const color = isIncrease
    ? ACCOUNT_COLORS.success
    : isDecrease
    ? ACCOUNT_COLORS.coral
    : ACCOUNT_COLORS.textSecondary;

  return (
    <Box
      sx={{
        p: 2,
        bgcolor: ACCOUNT_COLORS.white,
        borderRadius: 2,
        border: `2px solid ${ACCOUNT_COLORS.borderLight}`,
      }}
    >
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 700,
          color: ACCOUNT_COLORS.textSecondary,
          mb: 1,
        }}
      >
        {label}
      </Typography>

      <Stack direction="row" spacing={2} alignItems="center">
        {/* Previous */}
        <Box flex={1}>
          <Typography
            sx={{
              fontSize: 11,
              color: ACCOUNT_COLORS.textTertiary,
              mb: 0.5,
            }}
          >
            Previous
          </Typography>
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 700,
              color: ACCOUNT_COLORS.textSecondary,
              textDecoration: "line-through",
            }}
          >
            {previous.toFixed(0)}
            <Typography
              component="span"
              sx={{ fontSize: 12, ml: 0.5 }}
            >
              {unit}
            </Typography>
          </Typography>
        </Box>

        {/* Arrow/Icon */}
        <Box sx={{ color, display: "flex", alignItems: "center" }}>
          {icon}
        </Box>

        {/* Current */}
        <Box flex={1}>
          <Typography
            sx={{
              fontSize: 11,
              color: ACCOUNT_COLORS.textTertiary,
              mb: 0.5,
            }}
          >
            New
          </Typography>
          <Typography
            sx={{
              fontSize: 24,
              fontWeight: 900,
              color: ACCOUNT_COLORS.primary,
            }}
          >
            {current.toFixed(0)}
            <Typography
              component="span"
              sx={{ fontSize: 14, ml: 0.5 }}
            >
              {unit}
            </Typography>
          </Typography>
        </Box>
      </Stack>

      {/* Change Info */}
      {!isNoChange && (
        <Box
          sx={{
            mt: 1.5,
            pt: 1.5,
            borderTop: `1px solid ${ACCOUNT_COLORS.borderLight}`,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                bgcolor: color,
              }}
            />
            <Typography sx={{ fontSize: 12, fontWeight: 700, color }}>
              {isIncrease ? "+" : ""}
              {change.toFixed(0)} {unit}
            </Typography>
            <Typography
              sx={{
                fontSize: 12,
                color: ACCOUNT_COLORS.textTertiary,
              }}
            >
              ({isIncrease ? "+" : ""}
              {changePercent.toFixed(1)}%)
            </Typography>
          </Stack>
        </Box>
      )}
    </Box>
  );
}

export function ComparisonCard({ previous, current, comparison }: Props) {
  return (
    <Box
      sx={{
        p: 3,
        bgcolor: ACCOUNT_COLORS.blue,
        borderRadius: 2,
        border: `2px solid ${ACCOUNT_COLORS.info}`,
      }}
    >
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <ComparisonMetric
            label="Daily Grams"
            previous={previous.gramsPerDay}
            current={current.gramsPerDay}
            change={comparison.gramsChange}
            changePercent={comparison.gramsChangePercent}
            unit="g"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <ComparisonMetric
            label="Daily Calories"
            previous={previous.caloriesPerDay}
            current={current.caloriesPerDay}
            change={comparison.caloriesChange}
            changePercent={comparison.caloriesChangePercent}
            unit="cal"
          />
        </Grid>
      </Grid>
    </Box>
  );
}
